// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  TagsInput,
  Badge,
  ActionIcon,
  Progress,
  Tooltip,
  Checkbox,
  SimpleGrid,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconUpload,
  IconFile,
  IconDownload,
  IconEdit,
  IconTrash,
  IconSearch,
  IconFileDescription,
  IconPlus,
  IconX,
  IconHistory,
  IconFolder,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import {
  useDocuments,
  useUploadDocument,
  useUpdateDocument,
  useDeleteDocument,
  useCreateFolder,
} from '../../hooks/useDocuments';
import type { Document, DocumentCategory } from '../../types/document';

// ── Constants ───────────────────────────────────────────────────────

const DOCUMENT_CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'EMPLOYEE_FILE', label: 'Employee File' },
  { value: 'TAX_FORM', label: 'Tax Form' },
  { value: 'POLICY_DOCUMENT', label: 'Policy Document' },
  { value: 'HANDBOOK', label: 'Handbook' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'CERTIFICATION', label: 'Certification' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'PERFORMANCE_REVIEW', label: 'Performance Review' },
  { value: 'DISCIPLINARY', label: 'Disciplinary' },
  { value: 'BENEFITS', label: 'Benefits' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'OFFBOARDING', label: 'Offboarding' },
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'OTHER', label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
  EMPLOYEE_FILE: 'blue',
  TAX_FORM: 'red',
  POLICY_DOCUMENT: 'violet',
  HANDBOOK: 'indigo',
  CONTRACT: 'orange',
  CERTIFICATION: 'teal',
  TRAINING: 'cyan',
  PERFORMANCE_REVIEW: 'yellow',
  DISCIPLINARY: 'pink',
  BENEFITS: 'green',
  ONBOARDING: 'lime',
  OFFBOARDING: 'gray',
  COMPLIANCE: 'grape',
  OTHER: 'dark',
};

const ACCEPTED_MIME_TYPES = [
  MIME_TYPES.pdf,
  MIME_TYPES.doc,
  MIME_TYPES.docx,
  MIME_TYPES.xls,
  MIME_TYPES.xlsx,
  MIME_TYPES.csv,
  MIME_TYPES.png,
  MIME_TYPES.jpeg,
  MIME_TYPES.gif,
  'text/plain',
  'application/rtf',
];

// ── Helpers ─────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function categoryLabel(category: string): string {
  const found = DOCUMENT_CATEGORIES.find((c) => c.value === category);
  return found?.label ?? category;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix to get raw base64
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Row type ────────────────────────────────────────────────────────

interface DocumentRow extends Record<string, unknown> {
  id: string;
  originalId?: string;
  version?: number;
  isActive?: boolean;
  name: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  category: DocumentCategory;
  description?: string;
  employeeId?: string;
  employeeName?: string;
  tags: string[];
  storageKey: string;
  downloadUrl?: string;
  requiresAcknowledgment: boolean;
  acknowledgedCount?: number;
  totalRecipients?: number;
  uploadedBy: string;
  uploadedAt: string;
  createdAt: string;
}

// ── Component ───────────────────────────────────────────────────────

export default function DocumentListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DocumentRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentRow | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [folderOpen, setFolderOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Debounce search
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    const timeout = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  const { data: queryData, loading } = useDocuments({
    page,
    perPage: 25,
    category: categoryFilter || undefined,
    search: debouncedSearch || undefined,
  });
  const data = queryData;

  const uploadDoc = useUploadDocument();
  const updateDoc = useUpdateDocument();
  const deleteDoc = useDeleteDocument();
  const createFolder = useCreateFolder();

  // ── Upload form ─────────────────────────────────────────────────

  const uploadForm = useForm({
    initialValues: {
      name: '',
      description: '',
      category: 'OTHER' as string,
      employeeId: '',
      tags: [] as string[],
      requiresAcknowledgment: false,
    },
    validate: {
      name: (v: string) => (v && v.trim() ? null : 'Document name is required'),
      category: (v: string) => (v ? null : 'Category is required'),
    },
  });

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0] ?? null;
      setSelectedFile(file);
      if (file && !uploadForm.values.name) {
        uploadForm.setFieldValue('name', file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUploadSubmit = uploadForm.onSubmit(async (values) => {
    if (!selectedFile) {
      notifications.show({
        title: 'No file selected',
        message: 'Please select a file to upload.',
        color: 'red',
      });
      return;
    }

    try {
      const fileBase64 = await fileToBase64(selectedFile);

      await uploadDoc.mutateAsync({
        name: values.name,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type || 'application/octet-stream',
        category: values.category,
        description: values.description || undefined,
        employeeId: values.employeeId || undefined,
        tags: values.tags.length > 0 ? values.tags : undefined,
        requiresAcknowledgment: values.requiresAcknowledgment,
        fileBase64,
      });
      notifications.show({
        title: 'Document Uploaded',
        message: `"${values.name}" uploaded successfully.`,
        color: 'green',
      });
      setUploadOpen(false);
      uploadForm.reset();
      setSelectedFile(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to upload document';
      notifications.show({
        title: 'Upload Failed',
        message,
        color: 'red',
      });
    }
  });

  // ── Edit form ───────────────────────────────────────────────────

  const editForm = useForm({
    initialValues: {
      name: '',
      description: '',
      category: '',
      tags: [] as string[],
      requiresAcknowledgment: false,
    },
    validate: {
      name: (v: string) => (v && v.trim() ? null : 'Document name is required'),
      category: (v: string) => (v ? null : 'Category is required'),
    },
  });

  const openEdit = (row: DocumentRow) => {
    setEditTarget(row);
    editForm.setValues({
      name: row.name,
      description: row.description || '',
      category: row.category,
      tags: row.tags || [],
      requiresAcknowledgment: row.requiresAcknowledgment,
    });
  };

  const handleEditSubmit = editForm.onSubmit(async (values) => {
    if (!editTarget) return;
    try {
      await updateDoc.mutateAsync({
        id: editTarget.id,
        input: {
          name: values.name,
          category: values.category,
          description: values.description || undefined,
          tags: values.tags,
          requiresAcknowledgment: values.requiresAcknowledgment,
        },
      });
      notifications.show({
        title: 'Document Updated',
        message: 'A new version of this document metadata has been created (append-only).',
        color: 'green',
      });
      setEditTarget(null);
      editForm.reset();
    } catch (err) {
      notifications.show({
        title: 'Update Failed',
        message: err instanceof Error ? err.message : 'Failed to update document',
        color: 'red',
      });
    }
  });

  // ── Delete handler ──────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    try {
      await deleteDoc.mutateAsync({ id: deleteTarget.id, reason: deleteReason });
      notifications.show({
        title: 'Document Deleted',
        message: 'Document soft-deleted (new inactive version created for audit).',
        color: 'green',
      });
      setDeleteTarget(null);
      setDeleteReason('');
    } catch (err) {
      notifications.show({
        title: 'Delete Failed',
        message: err instanceof Error ? err.message : 'Failed to delete document',
        color: 'red',
      });
    }
  };

  // ── Folder handler ──────────────────────────────────────────────

  const folderForm = useForm({
    initialValues: { name: '' },
    validate: { name: (v: string) => (v && v.trim() ? null : 'Folder name is required') },
  });

  const handleCreateFolder = folderForm.onSubmit(async (values) => {
    try {
      await createFolder.mutateAsync({ name: values.name });
      notifications.show({
        title: 'Folder Created',
        message: `Folder "${values.name}" created.`,
        color: 'green',
      });
      setFolderOpen(false);
      folderForm.reset();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create folder',
        color: 'red',
      });
    }
  });

  // ── Table columns ───────────────────────────────────────────────

  const columns: Column<DocumentRow>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (row) => (
        <Group gap="xs" wrap="nowrap">
          <IconFileDescription size={16} color="var(--mantine-color-blue-6)" />
          <div>
            <Text size="sm" fw={500} lineClamp={1}>
              {row.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {row.fileName}
            </Text>
          </div>
        </Group>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      width: 150,
      sortable: true,
      render: (row) => (
        <Badge
          variant="light"
          color={CATEGORY_COLORS[row.category] || 'gray'}
          size="sm"
        >
          {categoryLabel(row.category)}
        </Badge>
      ),
    },
    {
      key: 'employeeName',
      label: 'Employee',
      width: 150,
      sortable: true,
      render: (row) => (
        <Text size="sm" c={row.employeeName ? undefined : 'dimmed'}>
          {row.employeeName || 'Company-wide'}
        </Text>
      ),
    },
    {
      key: 'fileSize',
      label: 'Size',
      width: 90,
      sortable: true,
      render: (row) => (
        <Text size="sm" c="dimmed">
          {formatFileSize(row.fileSize)}
        </Text>
      ),
    },
    {
      key: 'tags',
      label: 'Tags',
      width: 180,
      render: (row) =>
        row.tags && row.tags.length > 0 ? (
          <Group gap={4} wrap="wrap">
            {row.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" size="xs" color="gray">
                {tag}
              </Badge>
            ))}
            {row.tags.length > 3 && (
              <Text size="xs" c="dimmed">
                +{row.tags.length - 3}
              </Text>
            )}
          </Group>
        ) : (
          <Text size="xs" c="dimmed">--</Text>
        ),
    },
    {
      key: 'uploadedBy',
      label: 'Uploaded By',
      width: 120,
      sortable: true,
      render: (row) => <Text size="sm">{row.uploadedBy}</Text>,
    },
    {
      key: 'uploadedAt',
      label: 'Date',
      width: 110,
      sortable: true,
      render: (row) => (
        <Text size="sm" c="dimmed">
          {new Date(row.uploadedAt).toLocaleDateString()}
        </Text>
      ),
    },
    {
      key: 'acknowledgment',
      label: 'Ack.',
      width: 80,
      render: (row) => {
        if (!row.requiresAcknowledgment) {
          return <Text size="xs" c="dimmed">--</Text>;
        }
        const acked = row.acknowledgedCount ?? 0;
        const total = row.totalRecipients ?? 0;
        const pct = total > 0 ? Math.round((acked / total) * 100) : 0;
        return (
          <Tooltip label={`${acked} of ${total} acknowledged`}>
            <div>
              <Text size="xs" fw={500} ta="center">
                {acked}/{total}
              </Text>
              <Progress
                value={pct}
                size="xs"
                color={pct === 100 ? 'green' : pct > 50 ? 'yellow' : 'red'}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'version',
      label: 'Ver.',
      width: 60,
      render: (row) => (
        <Group gap={2}>
          <IconHistory size={12} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">v{row.version ?? 1}</Text>
        </Group>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────

  return (
    <Stack gap="md">
      <PageHeader
        title="Document Management"
        subtitle={data ? `${data.length} documents` : 'Upload, organize, and manage company documents'}
        breadcrumbs={[{ label: 'Documents' }]}
        actions={
          <Group gap="sm">
            <Button
              variant="light"
              icon={<IconFolder size={16} />}
              onClick={() => setFolderOpen(true)}
            >
              New Folder
            </Button>
            <Button
              icon={<IconUpload size={16} />}
              onClick={() => setUploadOpen(true)}
            >
              Upload Document
            </Button>
          </Group>
        }
      />

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search documents..."
          icon={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.currentTarget.value)}
          w={280}
        />
        <Select
          placeholder="All categories"
          clearable
          value={categoryFilter}
          onChange={(value) => {
            setCategoryFilter(value);
            setPage(1);
          }}
          data={DOCUMENT_CATEGORIES}
          w={200}
        />
      </Group>

      {/* Stats */}
      {data && data.length > 0 && (
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          <Paper p="sm" radius="sm" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Documents</Text>
            <Text size="xl" fw={700}>{data.length}</Text>
          </Paper>
          <Paper p="sm" radius="sm" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Categories</Text>
            <Text size="xl" fw={700}>
              {new Set((data as Document[]).map((d) => d.category)).size}
            </Text>
          </Paper>
          <Paper p="sm" radius="sm" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Pending Ack.</Text>
            <Text size="xl" fw={700}>
              {(data as Document[]).filter(
                (d) => d.requiresAcknowledgment && (d.acknowledgedCount ?? 0) < (d.totalRecipients ?? 0),
              ).length}
            </Text>
          </Paper>
          <Paper p="sm" radius="sm" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Size</Text>
            <Text size="xl" fw={700}>
              {formatFileSize(
                (data as Document[]).reduce((sum, d) => sum + d.fileSize, 0),
              )}
            </Text>
          </Paper>
        </SimpleGrid>
      )}

      {/* Document table */}
      <DataTable<DocumentRow>
        columns={columns}
        data={(data as DocumentRow[]) ?? []}
        loading={loading}
        pagination={
          data
            ? {
                page,
                pageSize: 25,
                total: data.length,
                onPageChange: setPage,
              }
            : undefined
        }
        getRowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/documents/${row.id}`)}
        emptyMessage="No documents found. Upload your first document to get started."
        actions={(row) => {
          const isActive = row.isActive !== false;
          return (
            <Group gap={4}>
              {row.downloadUrl && (
                <Tooltip label="Download">
                  <ActionIcon
                    variant="light"
                    size="sm"
                    color="blue"
                    component="a"
                    href={row.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <IconDownload size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              {isActive && (
                <>
                  <Tooltip label="Edit metadata (creates new version)">
                    <ActionIcon
                      variant="light"
                      size="sm"
                      color="yellow"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(row);
                      }}
                    >
                      <IconEdit size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete (soft-delete)">
                    <ActionIcon
                      variant="light"
                      size="sm"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(row);
                      }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </>
              )}
            </Group>
          );
        }}
      />

      {/* ── Upload Modal ──────────────────────────────────────────── */}
      <Modal
        opened={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          uploadForm.reset();
          setSelectedFile(null);
        }}
        title="Upload Document"
        size="lg"
      >
        <form onSubmit={handleUploadSubmit}>
          <Stack gap="md">
            <Dropzone
              onDrop={handleFileSelect}
              accept={ACCEPTED_MIME_TYPES}
              maxSize={50 * 1024 * 1024}
              multiple={false}
            >
              <Group
                justify="center"
                gap="xl"
                mih={120}
                style={{ pointerEvents: 'none' }}
              >
                <Dropzone.Accept>
                  <IconUpload size={40} stroke={1.5} color="var(--mantine-color-blue-6)" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={40} stroke={1.5} color="var(--mantine-color-red-6)" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFile size={40} stroke={1.5} color="var(--mantine-color-dimmed)" />
                </Dropzone.Idle>
                <div>
                  <Text size="sm" fw={500}>
                    {selectedFile
                      ? `Selected: ${selectedFile.name} (${formatFileSize(selectedFile.size)})`
                      : 'Drag a file here or click to browse'}
                  </Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    PDF, Word, Excel, CSV, images up to 50 MB
                  </Text>
                </div>
              </Group>
            </Dropzone>

            <TextInput
              label="Document Name"
              placeholder="e.g. Employee Handbook 2025"
              required
              {...uploadForm.getInputProps('name')}
            />

            <Select
              label="Category"
              required
              data={DOCUMENT_CATEGORIES}
              {...uploadForm.getInputProps('category')}
            />

            <TextInput
              label="Employee ID (optional)"
              placeholder="Leave blank for company-wide documents"
              {...uploadForm.getInputProps('employeeId')}
            />

            <Textarea
              label="Description"
              placeholder="Brief description of this document..."
              minRows={2}
              {...uploadForm.getInputProps('description')}
            />

            <TagsInput
              label="Tags"
              placeholder="Add tags..."
              data={uploadForm.values.tags}
              {...uploadForm.getInputProps('tags')}
            />

            <Checkbox
              label="Requires employee acknowledgment"
              description="Track which employees have reviewed this document"
              {...uploadForm.getInputProps('requiresAcknowledgment', { type: 'checkbox' })}
            />

            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                Append-only: This document will be stored immutably. Updates create new versions; deletions are soft-deletes preserved for audit.
              </Text>
            </Paper>

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setUploadOpen(false);
                  uploadForm.reset();
                  setSelectedFile(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                icon={<IconUpload size={16} />}
                loading={uploadDoc.isPending}
                disabled={!selectedFile}
              >
                Upload Document
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* ── Edit Metadata Modal ───────────────────────────────────── */}
      <Modal
        opened={editTarget !== null}
        onClose={() => {
          setEditTarget(null);
          editForm.reset();
        }}
        title="Edit Document Metadata (new version)"
        size="lg"
      >
        <form onSubmit={handleEditSubmit}>
          <Stack gap="md">
            <TextInput
              label="Document Name"
              required
              {...editForm.getInputProps('name')}
            />

            <Select
              label="Category"
              required
              data={DOCUMENT_CATEGORIES}
              {...editForm.getInputProps('category')}
            />

            <Textarea
              label="Description"
              placeholder="Brief description..."
              minRows={2}
              {...editForm.getInputProps('description')}
            />

            <TagsInput
              label="Tags"
              placeholder="Add tags..."
              data={editForm.values.tags}
              {...editForm.getInputProps('tags')}
            />

            <Checkbox
              label="Requires employee acknowledgment"
              {...editForm.getInputProps('requiresAcknowledgment', { type: 'checkbox' })}
            />

            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                Append-only: Saving will create a new version. The previous version remains immutable in the audit trail.
              </Text>
            </Paper>

            <Group justify="flex-end">
              <Button variant="default" onClick={() => { setEditTarget(null); editForm.reset(); }}>
                Cancel
              </Button>
              <Button type="submit" loading={updateDoc.isPending}>
                Save New Version
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* ── Delete Confirmation Modal ─────────────────────────────── */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteReason('');
        }}
        title="Delete Document"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will soft-delete{' '}
            <Text span fw={600}>
              {deleteTarget?.name}
            </Text>{' '}
            by creating a new inactive version (append-only). The original file is preserved for audit.
          </Text>
          <Textarea
            label="Reason for deletion"
            placeholder="Required for audit trail..."
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteReason('');
              }}
            >
              Keep Document
            </Button>
            <Button
              color="red"
              disabled={!deleteReason.trim()}
              loading={deleteDoc.isPending}
              onClick={handleDelete}
            >
              Delete Document
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Create Folder Modal ───────────────────────────────────── */}
      <Modal
        opened={folderOpen}
        onClose={() => {
          setFolderOpen(false);
          folderForm.reset();
        }}
        title="Create Folder"
        size="sm"
        centered
      >
        <form onSubmit={handleCreateFolder}>
          <Stack gap="md">
            <TextInput
              label="Folder Name"
              placeholder="e.g. 2025 Tax Forms"
              required
              {...folderForm.getInputProps('name')}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => { setFolderOpen(false); folderForm.reset(); }}>
                Cancel
              </Button>
              <Button
                type="submit"
                icon={<IconPlus size={16} />}
                loading={createFolder.isPending}
              >
                Create Folder
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
